import { useEffect } from 'react';

import { addUFOCustomMark } from '@atlaskit/react-ufo/custom-mark';
import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';

type UFOTransitionMarkFMPProps = {
	timestamp?: number;
};

/**
 * Marks FMP (First Meaningful Paint) on UFO transition interactions.
 *
 * When we are in a transition, this component places an
 * `addUFOCustomMark('fmp', timestamp)` call. Unlike `PageLoadMarkFMP` which is
 * meant for initial page loads, this component is specifically designed for SPA
 * transitions where the platform UFO library does not natively track FMP.
 *
 * The corresponding `metric:fmp` value is computed in `additionalPayloadData.ts` by
 * reading this mark from the interaction and calculating the offset from interaction start.
 */
const UFOTransitionMarkFMPComponent = ({ timestamp }: UFOTransitionMarkFMPProps) => {
	const isTransition = useIsTransition();

	useEffect(() => {
		if (isTransition) {
			addUFOCustomMark('fmp', timestamp);

			// The code below unfortunately relies on internals of the UFO library and on some
			// knowledge of how the UFO event data (especially FMP) are processed by the analytics
			// pipeline. This is relatively stable "private API", but it has to be sorted out with
			// the UFO team, as a follow up to https://product-fabric.atlassian.net/browse/CCP-7117.
			const interaction = getActiveInteraction();
			if (!interaction) {
				return;
			}

			// In UFO realm, there's no concept of FMP, and by design it is defaulted to TTAI. While
			// in Confluence we rely on FMP for transitions, so emulating the legacy behavior here.
			interaction.legacyMetrics = interaction.legacyMetrics || [];
			interaction.legacyMetrics.push({
				key: 'fmp',
				config: {
					type: 'PAGE_LOAD',
				},
				start: 0,
				stop: timestamp ?? null,
				type: 'PAGE_LOAD',
				custom: {},
			});
		}
	}, [isTransition, timestamp]);

	return null;
};

export const UFOTransitionMarkFMP = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(UFOTransitionMarkFMPComponent);
